import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, CardContent, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { Navigate, useLocation, useParams } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

function MetricCard({ label, value, helper }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
          {value}
        </Typography>
        {helper ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {helper}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function AdminGrowthDashboard() {
  const { user } = useAuth() || {};
  const { country } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  const isAdmin = useMemo(() => {
    const roles = user?.roles || [];
    return roles.some((r) => String(r || "").toLowerCase() === "admin");
  }, [user]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.admin.growthSummary(30);
        if (!mounted) return;
        setSummary(res?.data || null);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.error || "Failed to load growth summary.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (isAdmin) run();
    else setLoading(false);
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  if (!isAdmin) {
    const lang = country || (location.pathname.split("/")[1] || "en");
    return <Navigate to={`/${lang}/client/dashboard`} replace />;
  }

  const kpis = summary?.kpis || {};
  const funnel = summary?.funnel_counts || {};
  const stripe = summary?.stripe || {};
  const affiliate = summary?.affiliate || {};

  const conversionRate = Number(kpis.conversion_rate_pct || 0).toFixed(2);
  const activePremium = Number(kpis.active_premium_users || 0);
  const revenue30 = Number(stripe.revenue_last_period || 0).toFixed(2);
  const affiliateEst = Number(affiliate.estimation || 0).toFixed(2);
  const topCountry = kpis.top_country || "-";
  const guestToRegistered = Number(kpis.guest_to_registered_rate_pct || 0).toFixed(2);

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        Growth Intelligence Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        30-day summary from backend analytics, Stripe payments, and commission events.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard
              label="Conversion Rate (%)"
              value={`${conversionRate}%`}
              helper={`subscribe_success / meal_plan_generate (${funnel.subscribe_success || 0}/${funnel.meal_plan_generate || 0})`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard label="Active Premium Users" value={activePremium} />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard label="Revenue (last 30 days)" value={revenue30} helper="Raw total from successful payments." />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard label="Affiliate Estimation" value={affiliateEst} helper="estimated + confirmed commission amounts." />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard label="Top Country" value={topCountry} />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MetricCard label="Guest -> Registered rate" value={`${guestToRegistered}%`} />
          </Grid>
        </Grid>
      ) : null}
    </Container>
  );
}
